import { createAvatar } from '@dicebear/core';
import * as lorelei from '@dicebear/lorelei';
import * as notionists from '@dicebear/notionists';
import * as openPeeps from '@dicebear/open-peeps';
// Mapping of style names to their actual style modules
const STYLES = {
    lorelei: lorelei,
    notionists: notionists,
    'open-peeps': openPeeps,
};
/**
 * Deterministically generates a seed from demographics to ensure the same user always gets the same avatar.
 */
function generateSeed(demographics) {
    if (demographics.seed)
        return demographics.seed;
    if (demographics.id)
        return demographics.id;
    // Create a deterministic string from available attributes
    const parts = [
        demographics.gender ?? 'unknown',
        demographics.age?.toString() ?? 'unknown',
        demographics.skinTone ?? 'unknown',
    ];
    return parts.join('-');
}
/**
 * Maps synthetic demographics to specific DiceBear options based on the chosen style.
 * This is where we ensure the visual representation loosely matches the data where possible.
 */
function mapDemographicsToOptions(style, demographics) {
    const options = {};
    // NOTE: Because DiceBear's CC0 collections have varying parameter names, we do custom mapping per style.
    // We can expand these as needed.
    switch (style) {
        case 'lorelei':
            // Example basic mapping for lorelei
            if (demographics.skinTone === 'dark') {
                options.skinColor = ['a05b32', '8e471c', '723916'];
            }
            else if (demographics.skinTone === 'medium') {
                options.skinColor = ['e2a275', 'c77b4c'];
            }
            else if (demographics.skinTone === 'light') {
                options.skinColor = ['fcecdb', 'f5d0b5', 'efb892'];
            }
            break;
        case 'notionists':
            if (demographics.skinTone === 'dark') {
                options.skinColor = ['8e471c', '723916'];
            }
            else if (demographics.skinTone === 'medium') {
                options.skinColor = ['c77b4c'];
            }
            else if (demographics.skinTone === 'light') {
                options.skinColor = ['f5d0b5'];
            }
            break;
        case 'open-peeps':
            if (demographics.skinTone === 'dark') {
                options.skinColor = ['614335', '73412f'];
            }
            else if (demographics.skinTone === 'medium') {
                options.skinColor = ['d08b5b'];
            }
            else if (demographics.skinTone === 'light') {
                options.skinColor = ['ffdbb4', 'edb98a'];
            }
            // Open peeps allows mapping facial hair, hair, etc based on gender if desired
            break;
    }
    return options;
}
/**
 * Generates an SVG string for a synthetic user avatar.
 *
 * @param demographics The synthetic user's demographic data
 * @param options Styling and size options
 * @returns A raw SVG string
 */
export function generateAvatarSvg(demographics, options = {}) {
    const styleName = options.style ?? 'lorelei';
    const styleModule = STYLES[styleName];
    const seed = generateSeed(demographics);
    const mappedOptions = mapDemographicsToOptions(styleName, demographics);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const avatar = createAvatar(styleModule, {
        seed,
        size: options.size ?? 128,
        backgroundColor: options.backgroundColor,
        radius: options.radius,
        ...mappedOptions,
    });
    return avatar.toString();
}
/**
 * Generates a base64 Data URI for a synthetic user avatar, useful for <img> tags.
 *
 * @param demographics The synthetic user's demographic data
 * @param options Styling and size options
 * @returns A base64 SVG data URI
 */
export function generateAvatarDataUri(demographics, options = {}) {
    const styleName = options.style ?? 'lorelei';
    const styleModule = STYLES[styleName];
    const seed = generateSeed(demographics);
    const mappedOptions = mapDemographicsToOptions(styleName, demographics);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const avatar = createAvatar(styleModule, {
        seed,
        size: options.size ?? 128,
        backgroundColor: options.backgroundColor,
        radius: options.radius,
        ...mappedOptions,
    });
    return avatar.toDataUri();
}
export * from './types.js';
//# sourceMappingURL=index.js.map